import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DOMPurify from 'dompurify';
import { 
  faSave, 
  faTimes, 
  faPencilAlt,
  faPlus,
  faTrash,
  faCode,
  faEye,
  faArrowUp,
  faArrowDown
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import { doc, setDoc, collection } from 'firebase/firestore';
import { db } from '../../firebase';
import useSiteData from '../../hooks/useSiteData';

const DEFAULT_ICONS = {
  dineIn: `<svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.701 2.701 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zm-3-9v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2h12z" />
  </svg>`,
  takeaway: `<svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
  </svg>`,
  catering: `<svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>`,
  halal: `<svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.0822 10.898V9M14.0822 10.898V11.9806M14.0822 10.898C14.0822 11.3554 14.0822 11.711 14.0962 12M16.234 11.5C16.2754 11.484 16.2964 11.4758 16.3152 11.4691C17.0292 11.2135 17.8037 11.5222 18.2322 12.2333C18.2435 12.252 18.2557 12.2732 18.28 12.3156L18.2994 12.3494C18.3466 12.4318 18.3703 12.473 18.3846 12.5019C18.7017 13.1431 18.3306 13.9486 17.6955 13.9978C17.6669 14 17.6243 14 17.5391 14H16.7521C15.8675 14 15.4252 14 15.0817 13.8156C14.7104 13.6164 14.4124 13.2701 14.2409 12.8387C14.1528 12.6172 14.1136 12.3604 14.0962 12M14.0822 11.9806V12C14.0822 12.4231 14.0822 12.7501 14.0699 13.0155M14.0822 11.9806L14.0962 12M11.5 15C12.3021 15 12.7031 15 13.0195 14.8478C13.4413 14.6448 13.7764 14.2554 13.9511 13.7654C14.0227 13.5647 14.0552 13.3347 14.0699 13.0155M14.0699 13.0155L11.9304 10.5M9.5 9V13C9.5 14.1046 8.60457 15 7.5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M10.2791 3.35212C10.8428 2.74898 11.1246 2.44741 11.4257 2.27941C12.0935 1.90686 12.9065 1.90686 13.5743 2.27941C13.8754 2.44741 14.1572 2.74898 14.7209 3.35212C14.9749 3.62385 15.1019 3.75972 15.2431 3.8688C15.5532 4.10826 15.9214 4.26079 16.3099 4.31069C16.487 4.33343 16.6728 4.32715 17.0445 4.31459C17.8696 4.2867 18.2822 4.27276 18.6139 4.36689C19.3495 4.57561 19.9244 5.15054 20.1331 5.88612C20.2272 6.21784 20.2133 6.63038 20.1854 7.45546C20.1728 7.82718 20.1666 8.01305 20.1893 8.19006C20.2392 8.57861 20.3917 8.94684 20.6312 9.25688C20.7403 9.39812 20.8761 9.5251 21.1479 9.77907C21.751 10.3428 22.0526 10.6246 22.2206 10.9257C22.5931 11.5935 22.5931 12.4065 22.2206 13.0743C22.0526 13.3754 21.751 13.6572 21.1479 14.2209C20.8761 14.4749 20.7403 14.6019 20.6312 14.7431C20.3917 15.0532 20.2392 15.4214 20.1893 15.8099C20.1666 15.987 20.1728 16.1728 20.1854 16.5445C20.2133 17.3696 20.2272 17.7822 20.1331 18.1139C19.9244 18.8495 19.3495 19.4244 18.6139 19.6331C18.2822 19.7272 17.8696 19.7133 17.0445 19.6854C16.6728 19.6728 16.487 19.6666 16.3099 19.6893C15.9214 19.7392 15.5532 19.8917 15.2431 20.1312C15.1019 20.2403 14.9749 20.3761 14.7209 20.6479C14.1572 21.251 13.8754 21.5526 13.5743 21.7206C12.9065 22.0931 12.0935 22.0931 11.4257 21.7206C11.1246 21.5526 10.8428 21.251 10.2791 20.6479C10.0251 20.3761 9.89812 20.2403 9.75688 20.1312C9.44684 19.8917 9.07861 19.7392 8.69006 19.6893C8.51305 19.6666 8.32718 19.6728 7.95546 19.6854C7.13038 19.7133 6.71784 19.7272 6.38612 19.6331C5.65054 19.4244 5.07561 18.8495 4.86689 18.1139C4.77276 17.7822 4.7867 17.3696 4.81459 16.5445C4.82715 16.1728 4.83343 15.987 4.81069 15.8099C4.76079 15.4214 4.60826 15.0532 4.3688 14.7431C4.25972 14.6019 4.12385 14.4749 3.85212 14.2209C3.24898 13.6572 2.94741 13.3754 2.77941 13.0743C2.40686 12.4065 2.40686 11.5935 2.77941 10.9257C2.94741 10.6246 3.24898 10.3428 3.85212 9.77907C4.12385 9.5251 4.25972 9.39812 4.3688 9.25688C4.60826 8.94684 4.76079 8.5786 4.81069 8.19006C4.83343 8.01305 4.82715 7.82718 4.81459 7.45546C4.7867 6.63038 4.77276 6.21784 4.86689 5.88612C5.07561 5.15054 5.65054 4.57561 6.38612 4.36689C6.71784 4.27276 7.13038 4.2867 7.95546 4.31459C8.32718 4.32715 8.51305 4.33343 8.69006 4.31069C9.0786 4.26079 9.44684 4.10826 9.75688 3.8688C9.89812 3.75972 10.0251 3.62385 10.2791 3.35212Z" stroke="currentColor" stroke-width="1.5" />
</svg>
  `
};

const EditServices = () => {
  const { data, isLoading: isSiteDataLoading, refetch } = useSiteData();
  const [formData, setFormData] = useState({
    items: [
      {
        title: '',
        description: '',
        svgIcon: DEFAULT_ICONS.dineIn,
        previewMode: false,
        sortOrder: 1
      }
    ]
  });
  
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (data?.services) {
      setFormData({ items: data.services.map(service => ({
        ...service,
        previewMode: false
      }))});
    }
  }, [data]);

  const sanitizeSvg = (svg) => {
    return DOMPurify.sanitize(svg, {
      USE_PROFILES: { svg: true, svgFilters: true },
      ALLOWED_TAGS: ['svg', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'g', 'defs', 'title'],
      ALLOWED_ATTR: ['viewBox', 'width', 'height', 'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'd', 'transform', 'class']
    });
  };

  const handleServiceChange = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        [field]: field === 'svgIcon' ? sanitizeSvg(value) : value
      };
      return { ...prev, items: newItems };
    });
  };

  const handlePresetIconSelect = (index, iconKey) => {
    handleServiceChange(index, 'svgIcon', DEFAULT_ICONS[iconKey]);
  };

  const addService = () => {
    const maxSortOrder = Math.max(...formData.items.map(item => item.sortOrder), 0);
    setFormData(prev => ({
      items: [
        ...prev.items,
        {
          title: '',
          description: '',
          svgIcon: DEFAULT_ICONS.dineIn,
          previewMode: false,
          sortOrder: maxSortOrder + 1
        }
      ].sort((a, b) => a.sortOrder - b.sortOrder)
    }));
  };

  const moveService = (index, direction) => {
    const newItems = [...formData.items];
    const currentItem = newItems[index];
    const adjacentItem = newItems[index + direction];

    if (adjacentItem) {
      // Swap sort orders
      const tempOrder = currentItem.sortOrder;
      currentItem.sortOrder = adjacentItem.sortOrder;
      adjacentItem.sortOrder = tempOrder;

      // Sort the array by sortOrder
      newItems.sort((a, b) => a.sortOrder - b.sortOrder);
      setFormData(prev => ({ ...prev, items: newItems }));
    }
  };

  const removeService = (index) => {
    setFormData(prev => {
      const newItems = prev.items.filter((_, i) => i !== index);
      // Reorder remaining items
      return {
        items: newItems.map((item, i) => ({
          ...item,
          sortOrder: i + 1
        }))
      };
    });
  };

  const renderReadOnlyMode = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Services Section</h2>
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 flex items-center"
          >
            <FontAwesomeIcon icon={faPencilAlt} className="mr-2" />
            Edit
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {formData.items.map((service, index) => (
            <div key={index} className="bg-gray-50 p-6 rounded-lg">
              <div className="flex justify-center mb-4">
                <div 
                  className="w-16 h-16 text-primary"
                  dangerouslySetInnerHTML={{ __html: service.svgIcon }}
                />
              </div>
              <h3 className="text-xl font-semibold text-center mb-2">{service.title}</h3>
              <p className="text-gray-600 text-center">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderEditingMode = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Edit Services</h2>
          <button
            onClick={addService}
            className="px-4 py-2 text-green-600 bg-green-50 rounded-md hover:bg-green-100 flex items-center"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add Service
          </button>
        </div>

        <div className="space-y-6">
          {formData.items.map((service, index) => (
            <div
              key={index}
              className="p-6 border border-gray-200 rounded-lg bg-white"
            >
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-gray-500 font-medium">#{service.sortOrder}</span>
                  <h3 className="text-lg font-medium">Service</h3>
                </div>
                <div className="flex gap-2">
                  <div className="flex gap-1 mr-4">
                    <button
                      onClick={() => moveService(index, -1)}
                      disabled={index === 0}
                      className={`p-2 rounded ${index === 0 ? 'text-gray-300' : 'text-gray-600 hover:bg-gray-100'}`}
                      title="Move up"
                    >
                      <FontAwesomeIcon icon={faArrowUp} />
                    </button>
                    <button
                      onClick={() => moveService(index, 1)}
                      disabled={index === formData.items.length - 1}
                      className={`p-2 rounded ${index === formData.items.length - 1 ? 'text-gray-300' : 'text-gray-600 hover:bg-gray-100'}`}
                      title="Move down"
                    >
                      <FontAwesomeIcon icon={faArrowDown} />
                    </button>
                  </div>
                  <button
                    onClick={() => togglePreviewMode(index)}
                    className="text-blue-600 hover:text-blue-800 p-2"
                    title={service.previewMode ? 'Show code' : 'Show preview'}
                  >
                    <FontAwesomeIcon icon={service.previewMode ? faCode : faEye} />
                  </button>
                  <button
                    onClick={() => removeService(index)}
                    className="text-red-500 hover:text-red-700 p-2"
                    disabled={formData.items.length === 1}
                    title="Remove service"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>

              <div className="grid gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={service.title}
                    onChange={(e) => handleServiceChange(index, 'title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter service title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={service.description}
                    onChange={(e) => handleServiceChange(index, 'description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="Enter service description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    {Object.entries(DEFAULT_ICONS).map(([key, svg]) => (
                      <button
                        key={key}
                        onClick={() => handlePresetIconSelect(index, key)}
                        className={`p-4 border rounded-lg hover:bg-gray-50 ${
                          service.svgIcon === svg ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                        }`}
                      >
                        <div dangerouslySetInnerHTML={{ __html: svg }} />
                        <div className="text-xs text-gray-500 mt-2 capitalize">{key}</div>
                      </button>
                    ))}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom SVG {service.previewMode ? '(Preview)' : '(Code)'}
                    </label>
                    {service.previewMode ? (
                      <div className="w-full h-32 flex items-center justify-center border rounded-lg bg-white">
                        <div dangerouslySetInnerHTML={{ __html: service.svgIcon }} />
                      </div>
                    ) : (
                      <textarea
                        value={service.svgIcon}
                        onChange={(e) => handleServiceChange(index, 'svgIcon', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                        rows={5}
                        placeholder='<svg ... ></svg>'
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end space-x-4 sticky bottom-0 bg-gray-50 py-4">
        <button
          onClick={() => setIsEditing(false)}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <FontAwesomeIcon icon={faTimes} className="mr-2" />
          Cancel
        </button>
        <button
          onClick={() => setShowPreview(true)}
          className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          <FontAwesomeIcon icon={faSave} className="mr-2" />
          Preview Changes
        </button>
      </div>
    </div>
  );

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const docRef = doc(collection(db, 'services'), 'singleton');
      
      // Clean up data before saving
      const cleanedItems = formData.items
        .map(({ previewMode, ...item }) => item)
        .sort((a, b) => a.sortOrder - b.sortOrder);

      await setDoc(docRef, { items: cleanedItems });
      
      toast.success('Services updated successfully!');
      setError('');
      setIsEditing(false);
      setShowPreview(false);
      refetch();
    } catch (err) {
      console.error('Error updating services:', err);
      setError('Failed to update services.');
      toast.error('Failed to update services.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-1 min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold mb-6">Edit Services Section</h1>
        
        {error && (
          <div className="bg-red-50 text-red-500 p-4 rounded-lg mb-6 flex items-center animate-fade-in">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        {!showPreview ? (
          isEditing ? (
            renderEditingMode()
          ) : (
            renderReadOnlyMode()
          )
        ) : (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                  Preview Changes
                </h3>
                <div className="mt-2 max-h-[60vh] overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {formData.items.map((service, index) => (
                      <div key={index} className="bg-gray-50 p-6 rounded-lg">
                        <div className="flex justify-center mb-4">
                          <div 
                            className="w-16 h-16 text-primary"
                            dangerouslySetInnerHTML={{ __html: service.svgIcon }}
                          />
                        </div>
                        <h3 className="text-xl font-semibold text-center mb-2">{service.title}</h3>
                        <p className="text-gray-600 text-center">{service.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end space-x-4 mt-6 pt-4 border-t">
                  <button
                    onClick={() => setShowPreview(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                    Edit More
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-400"
                  >
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    {isLoading ? 'Saving...' : 'Confirm & Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditServices; 